import { useAtom } from "jotai";
import { useCallback, useEffect, useMemo, useState } from "react";
import ReactInputMask from "react-input-mask";

import { validatePhoneNumber } from "../../../shared/utils";
import { smsAtom } from "../../widget.state";
import { OptionType, SmsType } from "../../widget.types";

import { QuestionButton } from "./question-button.component";

export function QuestionSms({
  brandName,
  question,
  tacSmsBrand,
  smsBrandPrivacyPolicy,
  isAccepted,
  isTacAccepted,
  isDisabled,
  setQuestion,
  canClick,
}: SmsType) {
  const [sms, setSms] = useAtom(smsAtom);
  const [phoneNumber, setPhoneNumber] = useState("");
  const [phoneNumberError, setPhoneNumberError] = useState(false);

  const smsButtons: OptionType[] = useMemo(() => {
    return [
      {
        id: "true",
        title: "Yes, sign me up.",
        isCorrect: true,
        isSelected: isAccepted !== null && isAccepted,
      },
      {
        id: "false",
        title: "No, thanks.",
        isCorrect: false,
        isSelected: isAccepted !== null && !isAccepted,
      },
    ];
  }, [isAccepted]);

  const termsUrl = useMemo(() => {
    const form = document.querySelector("#FormSweepstake");
    const formPath = form ? form.getAttribute("data-form-path-url") : null;
    if (!formPath) {
      return "";
    }
    return `/pages/${formPath.split("/").reverse()[0]}-terms-conditions`;
  }, []);

  const handleGetExtraEntries = useCallback(
    (accepted: boolean) => {
      if (!canClick() || isDisabled) return;

      setQuestion((q) => ({
        ...q,
        smsInfo: {
          ...q.smsInfo!,
          isAccepted: accepted,
          isTacAccepted: false,
        },
      }));
    },
    [isDisabled, setQuestion, canClick]
  );

  const handleTACToggle = useCallback(() => {
    setQuestion((q) => ({
      ...q,
      smsInfo: {
        ...q.smsInfo!,
        isTacAccepted: !q.smsInfo?.isTacAccepted,
      },
    }));
  }, [setQuestion]);

  const handlePhoneNumberChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setPhoneNumber(e.target.value);
    },
    []
  );

  useEffect(() => {
    if (!phoneNumber) return;
    const isValid = validatePhoneNumber(phoneNumber);
    setPhoneNumberError(!isValid);
    if (isValid) setSms((sms) => ({ ...sms, phoneNumber }));
  }, [phoneNumber, setSms]);

  useEffect(() => {
    setPhoneNumber(sms.phoneNumber);
  }, [sms.phoneNumber]);

  return (
    <div className="quiz--single-page__body__question__section question-sms-section">
      <div className="quiz--single-page__body__question__section__header">
        <div className="quiz--single-page__body__question__section__header__entries">
          +2
        </div>
        <div className="quiz--single-page__body__question__section__header__title">
          {question}
        </div>
      </div>
      <div className="quiz--single-page__body__question__section__body">
        <div className="quiz__buttons">
          {smsButtons.map((smsButton) => (
            <QuestionButton
              key={smsButton.id}
              data-testid="quiz-question-option"
              data-option-id={smsButton.id}
              isSelected={smsButton.isSelected}
              isCorrect={smsButton.isCorrect}
              isSms={true}
              isCpl={false}
              isReminder={false}
              isDisabled={isDisabled}
              onClick={() =>
                handleGetExtraEntries(Boolean(JSON.parse(smsButton.id)))
              }
            >
              {smsButton.title}
            </QuestionButton>
          ))}
        </div>

        {isAccepted && (
          <div>
            <div className="question-sms-section--input-text">
              Enter your mobile phone number to earn +2 entries when you opt-in
              to SMS Marketing from this brand
            </div>
            <ReactInputMask
              className="question-sms-section--input"
              disabled={isDisabled}
              mask="(999) 999-9999"
              placeholder="Mobile Phone Number"
              value={phoneNumber}
              onChange={handlePhoneNumberChange}
              style={{ width: "100%" }}
            />
            {phoneNumberError || !sms.phoneNumber ? (
              <div className="quiz__feedback quiz__feedback--incorrect">
                Please enter a valid phone number
              </div>
            ) : null}
          </div>
        )}
        {isAccepted && (
          <>
            <label className="quiz__label">
              <input
                disabled={isDisabled || phoneNumberError || !sms.phoneNumber}
                checked={isTacAccepted}
                type="checkbox"
                onChange={handleTACToggle}
              />
              <div className="question-sms-section--terms">
                By checking this box you agree to receive recurring automated
                promotional and personalized marketing text messages (e.g. cart
                reminders) from {brandName} at the cell number used when signing
                up. Consent is not a condition of any purchase. Reply HELP for
                help and STOP to cancel. Msg frequency varies. Msg & data rates
                may apply. View{" "}
                <a
                  href={tacSmsBrand!}
                  onClick={(event) => {
                    event.preventDefault();
                    window.open(tacSmsBrand!, "_blank");
                  }}
                >
                  Terms
                </a>
                ,{" "}
                <a
                  href={smsBrandPrivacyPolicy!}
                  onClick={(event) => {
                    event.preventDefault();
                    window.open(smsBrandPrivacyPolicy!, "_blank");
                  }}
                >
                  Privacy
                </a>
                , and{" "}
                <a
                  href={termsUrl}
                  onClick={(event) => {
                    event.preventDefault();
                    window.open(termsUrl, "_blank");
                  }}
                >
                  Giveaway Terms
                </a>
                .
              </div>
            </label>
            {!isTacAccepted ? (
              <div className="quiz__feedback quiz__feedback--incorrect">
                Please accept Terms &amp; Conditions
              </div>
            ) : null}
          </>
        )}
      </div>
    </div>
  );
}
